
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    debtType: '',
    debtAmount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    // Validate form
    if (!formData.name || !formData.email || !formData.phone || !formData.debtType) {
      setSubmitStatus('Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('Message must be 500 characters or less.');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5r7m0s98t1oshnre5d0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('Thank you! Your consultation request has been submitted. We will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          debtType: '',
          debtAmount: '',
          message: ''
        });
      } else {
        setSubmitStatus('There was an error submitting your request. Please try again or call us directly.');
      }
    } catch (error) {
      setSubmitStatus('There was an error submitting your request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Free <span className="text-orange-500">Consultation</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Take the first step towards financial freedom. Our licensed debt settlement specialists are ready to help you today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Schedule Your Free Consultation</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="debtType" className="block text-sm font-medium text-gray-700 mb-2">
                    Type of Debt *
                  </label>
                  <select
                    id="debtType"
                    name="debtType"
                    value={formData.debtType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select debt type</option>
                    <option value="Credit Card Debt">Credit Card Debt</option>
                    <option value="Personal Loans">Personal Loans</option>
                    <option value="Medical Bills">Medical Bills</option>
                    <option value="Merchant Cash Advance">Merchant Cash Advance</option>
                    <option value="Business Loans">Business Loans</option>
                    <option value="Multiple Debts">Multiple Debts</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="debtAmount" className="block text-sm font-medium text-gray-700 mb-2">
                    Total Debt Amount
                  </label>
                  <select
                    id="debtAmount"
                    name="debtAmount"
                    value={formData.debtAmount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select amount range</option>
                    <option value="Under $10,000">Under $10,000</option>
                    <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                    <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                    <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                    <option value="Over $100,000">Over $100,000</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your situation
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  placeholder="Please describe your debt situation and how we can help you..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus && (
                <div className={`p-4 rounded-lg text-sm ${
                  submitStatus.includes('Thank you') 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {submitStatus}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg text-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                  isSubmitting
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-orange-500 hover:bg-orange-600 text-white transform hover:scale-105'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Consultation'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Ready to take control of your financial future? Contact our experienced debt settlement team today. 
                We offer free consultations with no obligation or upfront fees.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-phone text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Call Us Now</h4>
                  <p className="text-gray-600">Available 24/7 for emergency consultations</p>
                  <a href="tel:1-800-DEBT-FREE" className="text-orange-500 hover:text-orange-600 text-xl font-bold">
                    1-800-DEBT-FREE
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-envelope text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Email Us</h4>
                  <p className="text-gray-600">Get a response within 2 hours</p>
                  <a href="mailto:info@britesolutions.com" className="text-orange-500 hover:text-orange-600 font-semibold">
                    info@britesolutions.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-clock text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Business Hours</h4>
                  <p className="text-gray-600">
                    Monday - Friday: 8:00 AM - 8:00 PM EST<br />
                    Saturday: 9:00 AM - 5:00 PM EST<br />
                    Sunday: Emergency consultations only
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-teal-600 rounded-2xl p-6 text-white">
              <h4 className="text-xl font-bold mb-3">Why Choose Brite Solutions?</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-orange-200"></i>
                  <span>Licensed & BBB Accredited</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-orange-200"></i>
                  <span>No Upfront Fees</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-orange-200"></i>
                  <span>16+ Years of Experience</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-orange-200"></i>
                  <span>$500M+ Successfully Settled</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
